import type { Bridge } from '@casa/bridge';
import { MAX_DRIVER_CODE_BYTES, type DriverFile } from '@casa/driver-host';
import { readZip, utf8Decode } from '@casa/util';
import { checkSourceUrl } from '../data/dashboard.ts';

// Where an installed driver comes from (dashboard-editor spec C-7, plan step 3.5): a folder, a zip
// file, or a web address, which gives either a zip file or the driver's `driver.json` with its
// module beside it. Each gives the files `readDriverPackage` takes; nothing is installed here.
// A web address is fetched through the bridge, as everything on the network is.

/** A driver package's files are the manifest and its module: `driver.json`, and `.js` or `.mjs`. */
const PACKAGE_FILE = /(?:^driver\.json|\.m?js)$/;

/** At most this much is downloaded or unpacked: the module's limit, and room for the rest. */
const MAX_PACKAGE_BYTES = MAX_DRIVER_CODE_BYTES + 1024 * 1024;

const FETCH_TIMEOUT_MS = 20_000;

/** The archive's or folder's files a package can use: at its top, or in its one folder. */
export function packageFiles(entries: readonly { name: string; bytes: Uint8Array }[]) : DriverFile[] {
    const paths = entries.map((entry) => entry.name.split('/').filter((part) => part !== ''));
    const tops = new Set(paths.filter((parts) => parts.length > 1).map((parts) => parts[0]));
    const hasRootFiles = paths.some((parts) => parts.length === 1);
    // Files at the top, or, when everything is in one folder (as zip tools pack a folder), in it.
    const depth = !hasRootFiles && tops.size === 1 ? 2 : 1;

    return entries
        .map((entry, index) => ({ parts: paths[index]!, bytes: entry.bytes }))
        .filter(({ parts }) => parts.length === depth && PACKAGE_FILE.test(parts.at(-1)!))
        .map(({ parts, bytes }) => ({ name: parts.at(-1)!, bytes }));
}

/** The files a chosen folder gives (the browser's folder input). */
export async function filesFromFolder(files: FileList) : Promise<DriverFile[]> {
    const entries: { name: string; bytes: Uint8Array }[] = [];
    for (const file of Array.from(files)) {
        entries.push({ name: file.webkitRelativePath || file.name, bytes: new Uint8Array(await file.arrayBuffer()) });
    }

    return packageFiles(entries);
}

/** The files in a zip file. */
export async function filesFromZip(bytes: Uint8Array) : Promise<DriverFile[]> {
    return packageFiles(await readZip(bytes, MAX_PACKAGE_BYTES));
}

const isZip = (bytes: Uint8Array) : boolean => bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;

async function download(shell: Bridge, url: string) : Promise<Uint8Array> {
    const response = await shell.net.http.request({ method: 'GET', url, timeoutMs: FETCH_TIMEOUT_MS });
    const chunks: Uint8Array[] = [];
    let length = 0;
    for await (const chunk of response.body) {
        length += chunk.byteLength;
        if (length > MAX_PACKAGE_BYTES) {
            throw new Error(`${url} is larger than ${Math.round(MAX_PACKAGE_BYTES / 1024 / 1024)} MB.`);
        }
        chunks.push(chunk);
    }
    if (response.status !== 200) {
        throw new Error(`${url} answered ${response.status}.`);
    }

    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.byteLength;
    }

    return bytes;
}

/**
 * The files a web address gives: a zip file, or a `driver.json`, whose module (`entry`, else
 * `driver.js`) is fetched from beside it.
 */
export async function filesFromUrl(shell: Bridge, address: string) : Promise<DriverFile[]> {
    const problem = checkSourceUrl(address);
    if (problem) {
        throw new Error(problem);
    }

    const url = new URL(address.trim()).href;
    const bytes = await download(shell, url);
    if (isZip(bytes)) {
        return filesFromZip(bytes);
    }

    let manifest: unknown;
    try {
        manifest = JSON.parse(utf8Decode(bytes));
    } catch {
        throw new Error(`${url} is neither a zip file nor a driver.json.`);
    }

    const entry = typeof manifest === 'object' && manifest !== null && typeof (manifest as { entry?: unknown }).entry === 'string' ? (manifest as { entry: string }).entry : 'driver.js';
    const entryUrl = new URL(entry, url);
    if (entryUrl.origin !== new URL(url).origin) {
        throw new Error(`The driver's module must be beside its driver.json, not at ${entryUrl.origin}.`);
    }

    return [
        { name: 'driver.json', bytes },
        { name: entry, bytes: await download(shell, entryUrl.href) },
    ];
}
